import { storableError } from 'util/errors';
import { removeAndAppendFromTransactionRefs } from 'containers/InboxPage/InboxPage.duck';
import { parse } from 'util/urlHelpers';
import { addMarketplaceEntities } from 'ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from 'util/data';
import { getProcess } from '../../../transactions/transaction';
import { PROCESS_NAME } from '../transactions/transactionProcessWaitlist';
import { deleteWaitlistApi } from '../api';
import { INBOX_PAGE_SIZE } from '../config/constants';
import generateApiQueryParams from '../utils/generateApiQueryParams';

const initialState = {
  joining: false,
  joined: false,
  joinError: null,
  transactionId: null,
  deleting: false,
  deleteError: null,
};

const JOIN_WAITLIST_REQUEST = 'app/waitlist/JOIN_WAITLIST_REQUEST';
const JOIN_WAITLIST_SUCCESS = 'app/waitlist/JOIN_WAITLIST_SUCCESS';
const JOIN_WAITLIST_ERROR = 'app/waitlist/JOIN_WAITLIST_ERROR';
const DELETE_WAITLIST_REQUEST = 'app/waitlist/DELETE_WAITLIST_REQUEST';
const DELETE_WAITLIST_SUCCESS = 'app/waitlist/DELETE_WAITLIST_SUCCESS';
const DELETE_WAITLIST_ERROR = 'app/waitlist/DELETE_WAITLIST_ERROR';
const CLEAR_WAITLIST_STATE = 'app/waitlist/CLEAR_WAITLIST_STATE';

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case JOIN_WAITLIST_REQUEST:
      return { ...state, joining: true, joined: false, joinError: null };
    case JOIN_WAITLIST_SUCCESS:
      return {
        ...state,
        joining: false,
        joined: true,
        transactionId: payload.transactionId,
        joinError: null,
      };
    case JOIN_WAITLIST_ERROR:
      return {
        ...state,
        joining: false,
        joined: false,
        transactionId: null,
        joinError: payload,
      };
    case DELETE_WAITLIST_REQUEST:
      return { ...state, deleting: true, deleteError: null };
    case DELETE_WAITLIST_SUCCESS:
      return {
        ...state,
        deleting: false,
        deleteError: null,
      };
    case DELETE_WAITLIST_ERROR:
      return {
        ...state,
        deleting: false,
        deleteError: payload,
      };
    case CLEAR_WAITLIST_STATE:
      return initialState;
    default:
      return state;
  }
}

// Action creators
const joinWaitlistRequest = () => ({ type: JOIN_WAITLIST_REQUEST });
const joinWaitlistSuccess = (transactionId) => ({
  type: JOIN_WAITLIST_SUCCESS,
  payload: { transactionId },
});
const joinWaitlistError = (e) => ({ type: JOIN_WAITLIST_ERROR, payload: e });
const deleteWaitlistRequest = () => ({ type: DELETE_WAITLIST_REQUEST });
const deleteWaitlistSuccess = () => ({ type: DELETE_WAITLIST_SUCCESS });
const deleteWaitlistError = (e) => ({ type: DELETE_WAITLIST_ERROR, payload: e });
const clearState = () => ({ type: CLEAR_WAITLIST_STATE });

// Action to clear waitlist state
export const clearWaitlistState = () => (dispatch) => {
  dispatch(clearState());
};

export const joinWaitlist =
  (listingId, params = {}) =>
  async (dispatch, getState, sdk) => {
    dispatch(joinWaitlistRequest());
    try {
      const { transitions, PROCESS_ALIAS: processAlias } = getProcess(PROCESS_NAME);
      const bodyParams = {
        transition: transitions.JOIN_WAITLIST,
        processAlias,
        params: {
          listingId,
          protectedData: params,
        },
      };

      const response = await sdk.transactions.initiate(bodyParams);
      const transactionId = response.data.data.id;
      dispatch(joinWaitlistSuccess(transactionId));

      return transactionId;
    } catch (e) {
      const error = storableError(e);
      return dispatch(joinWaitlistError(error));
    }
  };

export const deleteWaitlistTransaction =
  ({ txId, params, search }) =>
  async (dispatch, getState, sdk) => {
    dispatch(deleteWaitlistRequest());
    try {
      await deleteWaitlistApi(txId);
      const { page = 1 } = parse(search);
      const apiQueryParams = generateApiQueryParams({
        params,
        search,
        additionalQueryParams: {
          page: INBOX_PAGE_SIZE * page + 1,
          perPage: 1,
        },
      });
      const response = await sdk.transactions.query(apiQueryParams);
      const nextTx = denormalisedResponseEntities(response)[0];
      const nextTxId = nextTx?.id;
      dispatch(addMarketplaceEntities(response));
      dispatch(removeAndAppendFromTransactionRefs({ removedId: txId, appendId: nextTxId }));
      dispatch(deleteWaitlistSuccess());
      return txId;
    } catch (e) {
      dispatch(deleteWaitlistError(storableError(e)));
      throw e;
    }
  };

// Selectors
export const isJoiningWaitlist = (state) => state.waitlist.joining;
export const isWaitlistJoined = (state) => state.waitlist.joined;
export const getWaitlistTransactionId = (state) => state.waitlist.transactionId;
export const getWaitlistJoinError = (state) => state.waitlist.joinError;
export const isDeletingWaitlist = (state) => state.waitlist.deleting;
export const getWaitlistDeleteError = (state) => state.waitlist.deleteError;
